import { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import "./css/pdfPreviewModal.css";

const PdfPreviewModal = ({
  open,
  onClose,
  title = "Vista previa del PDF",
  previewFn,
  downloadFn,
  previewArgs,
  downloadLabel = "Descargar PDF",
}) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia?.("(max-width: 768px)").matches;

  useEffect(() => {
    if (!open || isMobile) return;

    let revokeUrl = null;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const blobUrl = await Promise.resolve(previewFn?.(previewArgs));
      if (cancelled) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      revokeUrl = blobUrl;
      setUrl(blobUrl);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
      setUrl(null);
    };
  }, [open, isMobile, previewFn, previewArgs]);

  useEffect(() => {
    if (!open || !isMobile) return;
    (async () => {
      try {
        await Promise.resolve(downloadFn?.(previewArgs));
      } finally {
        onClose?.();
      }
    })();
  }, [open, isMobile, downloadFn, previewArgs, onClose]);

  useEffect(() => {
    if (!open || isMobile) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, isMobile, onClose]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await Promise.resolve(downloadFn?.(previewArgs));
    } finally {
      setDownloading(false);
    }
  };

  if (!open || isMobile) return null;

  return (
    <div className="pdf-preview-backdrop" onClick={onClose}>
      <div
        className="pdf-preview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="pdf-preview-modal__header">
          <div className="pdf-preview-modal__title">
            <Icon icon="mdi:file-pdf-box" />
            <span>{title}</span>
          </div>
          <button
            type="button"
            className="pdf-preview-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        <div className="pdf-preview-modal__body">
          {loading || !url ? (
            <div className="pdf-preview-modal__loading">
              <div className="pdf-preview-modal__spinner" />
              <span>Generando vista previa...</span>
            </div>
          ) : (
            <iframe
              title={title}
              src={url}
              className="pdf-preview-modal__iframe"
            />
          )}
        </div>

        <div className="pdf-preview-modal__footer">
          <button
            type="button"
            className="pdf-preview-modal__btn pdf-preview-modal__btn--ghost"
            onClick={onClose}
          >
            Cerrar
          </button>
          <button
            type="button"
            className="pdf-preview-modal__btn pdf-preview-modal__btn--primary"
            onClick={handleDownload}
            disabled={loading || downloading || !downloadFn}
          >
            {downloading ? (
              <>
                <span className="pdf-preview-modal__spinner pdf-preview-modal__spinner--sm" />
                Descargando...
              </>
            ) : (
              <>
                <Icon icon="mdi:download" />
                {downloadLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
